import { useCallback, useMemo } from 'react';
import { create } from 'zustand';

export type ActiveItemType = 'document' | 'communication';

export type ActiveItem = {
  id: string;
  type: ActiveItemType;
};

interface ActiveItemStore {
  activeItems: Array<ActiveItem>;
  removeActiveItems: (args: { ids: Array<string>; type?: ActiveItemType }) => void;
  pruneActiveItems: (args: { visibleIds: Array<string>; type: ActiveItemType }) => void;
  setActiveItems: (args: { activeItems: Array<ActiveItem> }) => void;
  setActiveItemsByType: (args: {
    activeItems: Array<ActiveItem>;
    type: ActiveItem['type'];
  }) => void;
  clearActiveItems: () => void;
  toggleActiveItems: (args: {
    activeItems: Array<ActiveItem>;
    keepExistingSelection?: boolean;
  }) => void;
  isSelected: (props: ActiveItem) => boolean;
}

const findExistingItem = ({ items, item }: { items: Array<ActiveItem>; item: ActiveItem }) =>
  items.find((i) => i.id === item.id && i.type === item.type);

export const useActiveItemStore = create<ActiveItemStore>()((set, get) => ({
  activeItems: [],
  removeActiveItems: ({ ids, type }) => {
    const idsSet = new Set(ids);

    set(({ activeItems }) => ({
      activeItems: activeItems.filter(
        (item) => !idsSet.has(item.id) || (type != null && item.type !== type),
      ),
    }));
  },
  pruneActiveItems: ({ visibleIds, type }) => {
    const visibleIdsSet = new Set(visibleIds);

    set((state) => {
      const activeItems = state.activeItems.filter(
        (item) => item.type !== type || visibleIdsSet.has(item.id),
      );

      return activeItems.length === state.activeItems.length ? state : { activeItems };
    });
  },
  setActiveItems: ({ activeItems }) => {
    set({ activeItems });
  },
  setActiveItemsByType: ({ type, activeItems }) => {
    const state = get();
    set({
      activeItems: [...state.activeItems.filter((item) => item.type !== type), ...activeItems],
    });
  },
  clearActiveItems: () => set({ activeItems: [] }),
  toggleActiveItems: ({ activeItems: items, keepExistingSelection = true }) => {
    const state = get();

    const nextSelection = items.reduce<Array<ActiveItem>>((acc, curr) => {
      if (findExistingItem({ items: acc, item: curr })) {
        return acc.filter((item) => !(item.id === curr.id && item.type === curr.type));
      }

      return [...(keepExistingSelection ? acc : []), curr];
    }, state.activeItems);

    set({ activeItems: nextSelection });
  },
  isSelected: (item) => Boolean(findExistingItem({ items: get().activeItems, item })),
}));

export const useActiveItem = ({ type }: { type: ActiveItemType }) => {
  const {
    activeItems: _activeItems,
    setActiveItems: _setActiveItems,
    removeActiveItems: _removeActiveItems,
    pruneActiveItems: _pruneActiveItems,
    clearActiveItems,
    toggleActiveItems: _toggleActiveItems,
  } = useActiveItemStore();

  const setActiveItems = useCallback(
    (ids: Array<string>) => _setActiveItems({ activeItems: ids.map((id) => ({ id, type })) }),
    [type, _setActiveItems],
  );
  const toggleActiveItems = useCallback(
    (ids: Array<string>) => _toggleActiveItems({ activeItems: ids.map((id) => ({ id, type })) }),
    [type, _toggleActiveItems],
  );
  const removeActiveItems = useCallback(
    (ids: Array<string>) => _removeActiveItems({ ids, type }),
    [type, _removeActiveItems],
  );
  const pruneActiveItems = useCallback(
    (visibleIds: Array<string>) => _pruneActiveItems({ visibleIds, type }),
    [type, _pruneActiveItems],
  );
  const activeItems = useMemo(
    () => _activeItems.filter((item) => item.type === type),
    [_activeItems, type],
  );
  const activeItemId = useMemo(() => activeItems[0]?.id, [activeItems]);

  return {
    activeItems,
    activeItemId,
    setActiveItems,
    toggleActiveItems,
    removeActiveItems,
    pruneActiveItems,
    clearActiveItems,
  };
};
